type MemoryRequest<T = unknown> = {
  result?: T;
  error?: Error;
  onsuccess?: (() => void) | null;
  onerror?: (() => void) | null;
  onupgradeneeded?: (() => void) | null;
};

type MemoryTransaction = {
  oncomplete?: (() => void) | null;
  onabort?: (() => void) | null;
  onerror?: (() => void) | null;
};

const queueTask = (callback: () => void) => {
  window.setTimeout(callback, 0);
};

const completeTransaction = (transaction: MemoryTransaction) => {
  queueTask(() => transaction.oncomplete?.());
};

const createSuccessRequest = <T>(
  result: T,
  transaction?: MemoryTransaction,
): MemoryRequest<T> => {
  const request: MemoryRequest<T> = { result };
  queueTask(() => {
    request.onsuccess?.();
    if (transaction) completeTransaction(transaction);
  });
  return request;
};

class MemoryObjectStore {
  constructor(
    private readonly records: Map<IDBValidKey, unknown>,
    private readonly transaction: MemoryTransaction,
  ) {}

  get(key: IDBValidKey) {
    return createSuccessRequest(this.records.get(key), this.transaction);
  }

  put(value: unknown, key: IDBValidKey) {
    this.records.set(key, value);
    return createSuccessRequest(key, this.transaction);
  }

  delete(key: IDBValidKey) {
    this.records.delete(key);
    return createSuccessRequest(undefined, this.transaction);
  }

  clear() {
    this.records.clear();
    return createSuccessRequest(undefined, this.transaction);
  }

  getAllKeys() {
    return createSuccessRequest([...this.records.keys()], this.transaction);
  }

  getAll() {
    return createSuccessRequest([...this.records.values()], this.transaction);
  }
}

class MemoryDatabase {
  private readonly stores = new Map<string, Map<IDBValidKey, unknown>>();
  onclose?: (() => void) | null;

  createObjectStore(name: string) {
    if (!this.stores.has(name)) {
      this.stores.set(name, new Map<IDBValidKey, unknown>());
    }
    return new MemoryObjectStore(this.stores.get(name)!, {});
  }

  transaction(name: string) {
    if (!this.stores.has(name)) {
      this.stores.set(name, new Map<IDBValidKey, unknown>());
    }

    const transaction: MemoryTransaction = {};
    return {
      objectStore: () =>
        new MemoryObjectStore(this.stores.get(name)!, transaction),
    };
  }

  close() {
    this.onclose?.();
  }
}

const memoryDatabases = new Map<string, MemoryDatabase>();

const createMemoryIndexedDb = (): Pick<
  IDBFactory,
  "open" | "deleteDatabase"
> => ({
  open: (name: string) => {
    const isNew = !memoryDatabases.has(name);
    const database = memoryDatabases.get(name) ?? new MemoryDatabase();
    memoryDatabases.set(name, database);

    const request: MemoryRequest<MemoryDatabase> = { result: database };
    queueTask(() => {
      if (isNew) request.onupgradeneeded?.();
      request.onsuccess?.();
    });

    return request as unknown as IDBOpenDBRequest;
  },
  deleteDatabase: (name: string) => {
    memoryDatabases.delete(name);
    return createSuccessRequest(undefined) as unknown as IDBOpenDBRequest;
  },
});

const ensureIndexedDbGlobal = () => {
  if (typeof window === "undefined") return;
  if ("indexedDB" in window) return;

  Object.defineProperty(window, "indexedDB", {
    configurable: true,
    value: createMemoryIndexedDb(),
  });
};

ensureIndexedDbGlobal();
